import { useCallback } from "react";
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { storage } from "@data/firebase";
import { useAuth } from "@contexts/AuthContext";
import { useProjects } from "@contexts/ProjectContext";
import { projectStorageBasePath } from "@domain/project/paths";
import useFirestoreDoc from "@data/hooks/useFirestoreDoc";

function resizeImage(file, maxWidth = 1200, quality = 0.8) {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, maxWidth / img.width);
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob((blob) => resolve(blob), "image/jpeg", quality);
    };
    img.src = url;
  });
}

export default function useSpaceGallery() {
  const { user } = useAuth();
  const { activeProject } = useProjects();
  const [images, setImages] = useFirestoreDoc("space-gallery", []);

  const addImage = useCallback(
    async (file) => {
      if (!user || !activeProject || !file) return;
      const blob = await resizeImage(file);
      const id = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const path = `${projectStorageBasePath(activeProject)}/space-gallery/${id}.jpg`;
      const sref = storageRef(storage, path);
      await uploadBytes(sref, blob);
      const imageUrl = await getDownloadURL(sref);
      setImages((prev) => [
        ...prev,
        { id, imageUrl, storagePath: path, createdAt: new Date().toISOString() },
      ]);
    },
    [user, activeProject, setImages]
  );

  const removeImage = useCallback(
    async (item) => {
      if (!user || !item) return;
      try {
        await deleteObject(storageRef(storage, item.storagePath));
      } catch (err) {
        console.warn("[useSpaceGallery] storage delete failed", err);
      }
      setImages((prev) => prev.filter((img) => img.id !== item.id));
    },
    [user, setImages]
  );

  return { images, addImage, removeImage };
}
